import React, { useEffect, useState } from 'react';
import ErrorWrapper from '@/app/ErrorWrapper';
import { Nav, NavLink, NavItem, Spinner } from 'reactstrap';
import classnames from 'classnames';
import { authApi, documentsApi, filesApi, studentsApi, teachersApi } from '../../api';
import { useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import moment from 'moment';
import { Button, notification, Table } from 'antd';
import { isoDateStringToYYYYMMDDhhmmss } from '@/utils/functions';
import { Avatar } from '@/components/Avatar/Avatar';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { getProfile } from './store/profileThunk';
import { ValidationCriteria } from '@/components';
import { useTranslation } from 'react-i18next'; // Thêm import
import { DOCUMENT_TYPE } from '@/utils/constants';

const ProfileComponent = ({ ...props }) => {
    const { t } = useTranslation(); // Thêm hook
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.profile);
    const authData = useSelector((state) => state.auth);
    const [activeTab, setActiveTab] = useState(3);
    const { handleSubmit, control, reset } = useForm({
        defaultValues: {
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            phone: user?.phone || '',
            email: user?.email || '',
            dateOfBirth: null,
            gender: user?.gender || 'Male',
            subject: user?.subject || '',
            major: user?.major || '',
            experience: user?.experience || '',
            bio: user?.bio || ''
        }
    });
    const enrollments = user?.enrollments || [];
    const tokens = user?.tokens || [];
    const tokensUsed = user?.tokensUsed || [];
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 10,
        total: 0
    });
    const [avatarUrl, setAvatarUrl] = useState(user?.avatar || '');
    const [uploadedFile, setUploadedFile] = useState();
    const [changePassData, setChangePassData] = useState({
        currentPassword: '',
        newPassword: ''
    });
    const [comparePassword, setComparepassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [validation, setValidation] = useState({
        length: false,
        lowercase: false,
        uppercase: false,
        number: false,
        specialChar: false
    });

    const handleAvatarChange = (file) => {
        if (file instanceof File) {
            filesApi
                .upload({
                    fileName: file.name,
                    fileType: file.type,
                    entityName: authData?.roles[0]?.name == 'Student' ? 'students' : 'teachers'
                })
                .then((res) => {
                    notification.info({ message: t('Profile_Notify_UploadingAvatar') });
                    return axios.put(res?.data?.data?.url, file, {
                        headers: {
                            'Content-Type': file.type
                        }
                    });
                })
                .then(() => {
                    setUploadedFile(file);

                    filesApi
                        .view({
                            fileName: file.name,
                            entityName:
                                authData?.roles[0]?.name == 'Student' ? 'students' : 'teachers'
                        })
                        .then((res) => {
                            setAvatarUrl(res?.data?.data?.url);
                            notification.success({ message: t('Profile_Notify_AvatarUploaded') });
                        });
                })
                .catch((err) => {
                    console.error('Avatar upload error:', err);
                    notification.error({
                        message: t('Profile_Notify_AvatarUploadFailed'),
                        description:
                            err.response?.data?.message || t('Profile_Notify_CouldNotUpload')
                    });
                });
        }
    };

    const submitForm = async (formPayload) => {
        const apiToUse =
            authData?.roles[0].name === 'Student'
                ? studentsApi
                : authData?.roles[0].name === 'Teacher'
                ? teachersApi
                : null;

        if (!apiToUse) {
            console.error('Cannot determine user role for API call');
            notification.error({ message: t('Profile_Notify_UpdateFailedInvalidRole') });
            return;
        }

        try {
            const payload = {
                ...formPayload,
                id: user?.id
            };

            await apiToUse.update(payload);
            await documentsApi.create({
                name: 'Profile Avatar',
                fileType: uploadedFile.type,
                documentType: DOCUMENT_TYPE.AVATAR,
                fileName: uploadedFile.name,
                entityName: authData?.roles[0]?.name == 'Student' ? 'students' : 'teachers',
                entityId: user?.id
            })
            dispatch(getProfile({ ...user, roles: authData?.roles }));

            notification.success({ message: t('Profile_Notify_UpdateSuccess') });
        } catch (err) {
            console.error('Update or profile fetch failed:', err);
            notification.error({
                message: t('Profile_Notify_UpdateFailed'),
                description: err.response?.data?.message || t('Profile_Notify_PleaseTryAgain')
            });
        }
    };

    const handlePasswordChange = (e) => {
        const { value } = e.target;

        setChangePassData((prevData) => ({
            ...prevData,
            newPassword: value
        }));

        const hasLength = value.length >= 8;
        const hasLowercase = /[a-z]/.test(value);
        const hasUppercase = /[A-Z]/.test(value);
        const hasNumber = /[0-9]/.test(value);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);

        setValidation({
            length: hasLength,
            lowercase: hasLowercase,
            uppercase: hasUppercase,
            number: hasNumber,
            specialChar: hasSpecialChar
        });
    };

    const isPasswordValid =
        Object.values(validation).every(Boolean) &&
        changePassData.newPassword === comparePassword &&
        changePassData.currentPassword.length > 0;
    const handleChangePassword = () => {
        authApi
            .changePassword(changePassData)
            .then(() => {
                setChangePassData({
                    currentPassword: '',
                    newPassword: ''
                });
                setComparepassword('');
                notification.success({ message: t('Profile_Notify_PasswordChangeSuccess') });
            })
            .catch((err) => {
                const errText = err?.response?.data?.message || '';
                if (errText.includes('current password')) {
                    notification.info({ message: t('Profile_Notify_InvalidCurrentPassword') });
                } else {
                    notification.info({ message: t('Profile_Notify_PasswordChangeError') });
                }
                console.error(err);
            });
    };

    const columns = [
        {
            key: 'description',
            dataIndex: 'description',
            title: t('Profile_Table_Description'),
            render: (_, row) => row?.course?.description || ""
        },
        {
            key: 'startDate',
            dataIndex: 'startDate',
            title: t('Profile_Table_StartDate'),
            render: (_, row) => isoDateStringToYYYYMMDDhhmmss(row?.course?.startDate) || ""
        },
        {
            key: 'endDate',
            dataIndex: 'endDate',
            title: t('Profile_Table_EndDate'),
            render: (_, row) => isoDateStringToYYYYMMDDhhmmss(row?.course?.endDate) || ""
        },
        {
            key: 'teacher',
            dataIndex: 'teacher',
            title: t('Profile_Table_Teacher'),
            render: (_, row) => {
                return `${row?.course?.teacher?.user?.firstName || ""} ${row?.course?.teacher?.user?.lastName || ""}`;
            }
        },
        {
            key: 'attended',
            dataIndex: 'attended',
            title: t('Profile_Table_Attended'),
            render: (_, row) => {
                return `${row?.course?.attendedLessonsCount || 0}`;
            }
        },
        {
            key: 'remaining',
            dataIndex: 'remaining',
            title: t('Profile_Table_Remaining'),
            render: (_, row) => {
                return `${row?.course?.remainingLessonsCount || 0}`;
            }
        }
    ];

    const iconStyle = {
        position: 'absolute',
        top: '50%',
        right: '12px',
        transform: 'translateY(-50%)',
        cursor: 'pointer',
        color: '#555'
    };

    useEffect(() => {
        if (user) {
            reset({
                firstName: user?.firstName,
                lastName: user?.lastName,
                phone: user?.phone,
                email: user?.email,
                dateOfBirth: user?.dateOfBirth ? moment(user?.dateOfBirth).toDate() : null,
                gender: user?.gender,
                subject: user?.subject,
                major: user?.major,
                experience: user?.experience,
                onboardingDate: user?.onboardingDate ? moment(user?.onboardingDate).toDate() : null,
                bio: user?.bio
            });
        }
    }, [user, reset]);

    useEffect(() => {
        dispatch(getProfile({ ...user, roles: authData?.roles }));
    }, [dispatch, user?.id, pagination.page]);

    return (
        <>
            <div className="section-body">
                <div className="container-fluid">
                    <div className="d-flex justify-content-between align-items-center ">
                        <div className="header-action">
                            <h1 className="page-title">{t('Profile_Title')}</h1>
                            <ol className="breadcrumb page-breadcrumb">
                                <li className="breadcrumb-item">
                                    <a href>{t('Profile_Breadcrumb_Pages')}</a>
                                </li>
                                <li
                                    className="breadcrumb-item active"
                                    aria-current="page">
                                    {t('Profile_Title')}
                                </li>
                            </ol>
                        </div>
                        <Nav
                            tabs
                            className="page-header-tab">
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: activeTab === 3 })}
                                    onClick={() => setActiveTab(3)}>
                                    {t('Profile_Tab_Profile')}
                                </NavLink>
                            </NavItem>
                        </Nav>
                    </div>
                </div>
            </div>
            <div className="section-body mt-4">
                <div className="container-fluid">
                    <div className="row clearfix">
                        {tokens?.map((item, idx) => {
                            return (
                                <div
                                    className="col-lg-3 col-md-6"
                                    key={idx}>
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="widgets1">
                                                <div className="icon">
                                                    <i className="icon-trophy text-success font-30"></i>
                                                </div>
                                                <div className="details">
                                                    <h6 className="mb-0 font600">
                                                        {t(item?.tokenType?.name)}
                                                    </h6>
                                                    <span className="mb-0">{item?.credit}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        <div className="col-md-12">
                            <form
                                onSubmit={handleSubmit(submitForm)}
                                className="flex flex-col gap-2 lg:flex-row">
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">
                                            {t('Profile_EditProfile_Title')}
                                        </h3>
                                    </div>
                                    <div className="mx-auto flex flex-col items-center justify-center pt-3 pb-3">
                                        {' '}
                                        <label className="mb-2">
                                            {t('Profile_EditProfile_Avatar')}
                                        </label>
                                        <Avatar
                                            value={avatarUrl}
                                            onChange={(file) => handleAvatarChange(file)}
                                            size={100}
                                        />
                                    </div>
                                    <div className="card-body form-horizontal">
                                        <div className="form-group row">
                                            <label className="col-md-3 col-form-label">
                                                {t('Profile_EditProfile_FirstName')}
                                            </label>
                                            <div className="col-md-7">
                                                <Controller
                                                    control={control}
                                                    rules={{ required: true }}
                                                    render={({ field }) => (
                                                        <input
                                                            {...field}
                                                            type="text"
                                                            className="form-control"
                                                        />
                                                    )}
                                                    name="firstName"
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group row">
                                            <label className="col-md-3 col-form-label">
                                                {t('Profile_EditProfile_LastName')}
                                            </label>
                                            <div className="col-md-7">
                                                <Controller
                                                    control={control}
                                                    rules={{ required: true }}
                                                    render={({ field }) => (
                                                        <input
                                                            {...field}
                                                            type="text"
                                                            className="form-control"
                                                        />
                                                    )}
                                                    name="lastName"
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group row">
                                            <label className="col-md-3 col-form-label">
                                                {t('Profile_EditProfile_Email')}
                                            </label>
                                            <div className="col-md-7">
                                                <Controller
                                                    control={control}
                                                    rules={{ required: true }}
                                                    render={({ field }) => (
                                                        <input
                                                            {...field}
                                                            type="text"
                                                            className="form-control"
                                                        />
                                                    )}
                                                    name="email"
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group row">
                                            <label className="col-md-3 col-form-label">
                                                {t('Profile_EditProfile_Phone')}
                                            </label>
                                            <div className="col-md-7">
                                                <Controller
                                                    control={control}
                                                    rules={{ required: true }}
                                                    render={({ field }) => (
                                                        <input
                                                            {...field}
                                                            type="text"
                                                            className="form-control"
                                                        />
                                                    )}
                                                    name="phone"
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group row">
                                            <label className="col-md-3 col-form-label">
                                                {t('Profile_EditProfile_Gender')}
                                            </label>
                                            <div className="col-md-7">
                                                <Controller
                                                    control={control}
                                                    name="gender"
                                                    defaultValue="Male"
                                                    render={({
                                                        field: { onChange, onBlur, value }
                                                    }) => (
                                                        <select
                                                            className="form-control show-tick"
                                                            value={value}
                                                            onChange={onChange}
                                                            onBlur={onBlur}>
                                                            <option value="Male">
                                                                {t('Profile_EditProfile_Male')}
                                                            </option>
                                                            <option value="Female">
                                                                {t('Profile_EditProfile_Female')}
                                                            </option>
                                                        </select>
                                                    )}
                                                />
                                            </div>
                                        </div>
                                        {tokens.length > 0 && (
                                            <div className="form-group row">
                                                <label className="col-md-3 col-form-label">
                                                    {t('Profile_EditProfile_Token')}
                                                </label>
                                                <div className="col-md-7 flex items-center flex-col justify-center">
                                                    {tokens?.map((ele, idx) => {
                                                        return (
                                                            <div
                                                                className="w-full flex gap-2 items-center"
                                                                key={idx}>
                                                                <label className="mb-0">
                                                                    {t(ele?.tokenType?.name)}
                                                                </label>
                                                                <div className="">
                                                                    {ele?.credit}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                        {tokensUsed.length > 0 && (
                                            <div className="form-group row">
                                                <label className="col-md-3 col-form-label">
                                                    {t('Profile_EditProfile_TokenPurchased')}
                                                </label>
                                                <div className="col-md-7 flex items-center flex-col justify-center">
                                                    {tokensUsed?.map((ele, idx) => {
                                                        return (
                                                            <div
                                                                className="w-full flex gap-2 items-center"
                                                                key={idx}>
                                                                <label className="mb-0">
                                                                    {ele?.name}
                                                                </label>
                                                                <div className="">
                                                                    {ele?.totalAmount}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="card-footer text-right">
                                        <button
                                            type="submit"
                                            className="btn btn-primary">
                                            {t('Profile_EditProfile_UpdateBtn')}
                                        </button>
                                    </div>
                                </div>

                                <div className="col-lg-4 col-md-12 col-sm-12">
                                    <div className="row">
                                        <div className="col-12 col-md-6">
                                            <div className="form-group">
                                                <label className="form-label">
                                                    {t('Profile_ChangePass_Current')}
                                                    <span style={{ color: 'red' }}>*</span>
                                                </label>
                                                <div style={{ position: 'relative' }}>
                                                    <input
                                                        type={
                                                            showCurrentPassword
                                                                ? 'text'
                                                                : 'password'
                                                        }
                                                        className="form-control"
                                                        placeholder={t(
                                                            'Profile_ChangePass_Placeholder'
                                                        )}
                                                        value={changePassData.currentPassword}
                                                        onChange={(e) =>
                                                            setChangePassData((prevData) => ({
                                                                ...prevData,
                                                                currentPassword: e.target.value
                                                            }))
                                                        }
                                                    />
                                                    <i
                                                        className={
                                                            showCurrentPassword
                                                                ? 'fa fa-eye-slash'
                                                                : 'fa fa-eye'
                                                        }
                                                        onClick={() =>
                                                            setShowCurrentPassword(
                                                                !showCurrentPassword
                                                            )
                                                        }
                                                        style={iconStyle}
                                                        aria-hidden="true"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-12 col-md-6">
                                            <div className="form-group">
                                                <label className="form-label">
                                                    {t('Profile_ChangePass_New')}
                                                    <span style={{ color: 'red' }}>*</span>
                                                </label>
                                                <div style={{ position: 'relative' }}>
                                                    <input
                                                        type={showPassword ? 'text' : 'password'}
                                                        className="form-control"
                                                        placeholder={t(
                                                            'Profile_ChangePass_Placeholder'
                                                        )}
                                                        value={changePassData.newPassword}
                                                        onChange={handlePasswordChange}
                                                    />
                                                    <i
                                                        className={
                                                            showPassword
                                                                ? 'fa fa-eye-slash'
                                                                : 'fa fa-eye'
                                                        }
                                                        onClick={() =>
                                                            setShowPassword(!showPassword)
                                                        }
                                                        style={iconStyle}
                                                        aria-hidden="true"></i>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-6 pr-0">
                                            <div className="form-group">
                                                <label className="form-label">
                                                    {t('Profile_ChangePass_Confirm')}
                                                    <span style={{ color: 'red' }}>*</span>
                                                </label>
                                                <div style={{ position: 'relative' }}>
                                                    <input
                                                        type={
                                                            showConfirmPassword
                                                                ? 'text'
                                                                : 'password'
                                                        }
                                                        className="form-control"
                                                        placeholder={t(
                                                            'Profile_ChangePass_Placeholder'
                                                        )}
                                                        value={comparePassword}
                                                        onChange={(e) =>
                                                            setComparepassword(e.target.value)
                                                        }
                                                    />
                                                    <i
                                                        className={
                                                            showConfirmPassword
                                                                ? 'fa fa-eye-slash'
                                                                : 'fa fa-eye'
                                                        }
                                                        onClick={() =>
                                                            setShowConfirmPassword(
                                                                !showConfirmPassword
                                                            )
                                                        }
                                                        style={iconStyle}
                                                        aria-hidden="true"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row">
                                        {changePassData.newPassword.length > 0 && (
                                            <ul>
                                                <ValidationCriteria
                                                    isValid={validation.length}
                                                    text={t('Profile_Validation_Length')}
                                                />
                                                <ValidationCriteria
                                                    isValid={validation.lowercase}
                                                    text={t('Profile_Validation_Lowercase')}
                                                />
                                                <ValidationCriteria
                                                    isValid={validation.uppercase}
                                                    text={t('Profile_Validation_Uppercase')}
                                                />
                                                <ValidationCriteria
                                                    isValid={validation.number}
                                                    text={t('Profile_Validation_Number')}
                                                />
                                                <ValidationCriteria
                                                    isValid={validation.specialChar}
                                                    text={t('Profile_Validation_SpecialChar')}
                                                />
                                            </ul>
                                        )}
                                    </div>

                                    <div className="row flex justify-end">
                                        <Button
                                            className="btn btn-primary hover:!shadow-lg"
                                            disabled={!isPasswordValid}
                                            onClick={() => handleChangePassword()}>
                                            {t('Profile_ChangePass_Button')}
                                        </Button>
                                    </div>
                                </div>
                            </form>

                            {authData?.roles[0]?.name == 'Student' && (
                                <div className="card">
                                    <Table
                                        columns={columns}
                                        dataSource={enrollments}
                                        pagination={{
                                            pageSize: pagination.pageSize,
                                            current: pagination.page,
                                            total: pagination.total,
                                            onChange: (e) =>
                                                setPagination({ ...pagination, page: e })
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

ProfileComponent.displayName = 'Profile';

const Profile = (props) => {
    return (
        <ErrorWrapper>
            <ProfileComponent
                data-id="Profile-cpn"
                {...props}
            />
        </ErrorWrapper>
    );
};

export default Profile;
